'use client'

import { useCallback, useEffect, useMemo, type ComponentType, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import Container from '@/components/structure/Container'
import Filters from '@/components/ui/Filters'
import List, { type ListItemBase } from '@/components/content/common/List'
import Row from '@/components/content/common/Row'
import Categories from '@/components/content/common/Categories'
import ExploreButton from '@/components/content/common/ExploreButton'
import { cn, scrollToSection } from '@/lib/utils'
import { easeShort } from '@/lib/transitions'
import { usePageContext } from '@/components/content/common/PageContext'
import useHash from '@/hooks/useHash'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { FeaturedSection } from '@/types/app'
import type { Category } from '@/payload-types'

type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug'>

type FilterableContainerProps<T extends ListItemBase> = {
  collection?: string
  categories: CategoryFilter[]
  items: T[]
  featuredItems?: FeaturedSection[]
  element: ComponentType<{ item: T; type?: FeaturedTagType | string; index?: number }>
  listElement?: ComponentType<{ item: T }>
  filterItems?: (items: T[], filter: Pick<Category, 'id' | 'name' | 'slug'>) => T[]
  cols?: 3 | 2 | 1
  overlay?: boolean
  picker?: ReactNode
}

export default function FilterableContainer<T extends ListItemBase>({
  collection = 'items',
  categories,
  items,
  featuredItems,
  element,
  listElement,
  filterItems,
  cols = 3,
  overlay = true,
  picker,
}: FilterableContainerProps<T>) {
  const {
    selectedFilter,
    setSelectedFilter,
    selectedCategories,
    setSelectedCategories,
    query,
    setQuery,
  } = usePageContext()
  const { md } = useBreakpoint()
  const hash = useHash()
  const scrollToValues = useMemo(() => (overlay ? { mobile: 64, desktop: 56 } : 80), [overlay])
  const fold = overlay ? 639 : 260

  const filteredCategories = useMemo(() => {
    if (!filterItems) return categories
    return categories.filter((cat) => filterItems(items, cat).length > 0)
  }, [categories, items, filterItems])

  const isFeatured = selectedFilter?.slug === 'featured' && featuredItems
  const isAll = selectedFilter?.slug === `all`

  useEffect(() => {
    if (hash) {
      const matchingFilter = filteredCategories.find((filter) => filter.slug === hash)
      if (matchingFilter) {
        setSelectedCategories([matchingFilter])
        return
      }
    }
  }, [hash, filteredCategories, setSelectedFilter])

  const handleSelected = (selection: Pick<Category, 'id' | 'name' | 'slug'>) => {
    const filtersElement = document.getElementById('filters')
    window.history.pushState(null, '', `#${selection.slug}`)

    if (filtersElement) {
      const { top } = filtersElement.getBoundingClientRect()

      if (Math.abs(top) <= 5) {
        setSelectedFilter(selection)
      } else {
        scrollToSection({ id: 'filters', offset: scrollToValues, animate: true, hideHash: true })
        setSelectedFilter(selection)
      }
    } else {
      setSelectedFilter(selection)
    }
  }

  useEffect(() => {
    if ((selectedCategories && selectedCategories.length > 0) || query !== '') {
      setSelectedFilter({ id: 0, name: 'All', slug: 'all' })
    }
  }, [selectedCategories, setSelectedFilter, query])

  useEffect(() => {
    if (selectedFilter?.slug === 'featured') {
      setQuery('')
      setSelectedCategories([])
    }
  }, [selectedFilter?.slug, setQuery, setSelectedCategories])

  const scrollToFiltersIfNeeded = useCallback(() => {
    const filtersElement = document.getElementById('filters')
    if (!filtersElement) return
    const { top } = filtersElement.getBoundingClientRect()
    const responsiveTop = !md ? 64 : 32
    if (Math.abs(top) > responsiveTop) {
      scrollToSection({ id: 'filters', offset: scrollToValues, animate: true, hideHash: true })
    }
  }, [md, scrollToValues])

  useEffect(() => {
    if (!query) return
    scrollToFiltersIfNeeded()
  }, [query, scrollToFiltersIfNeeded])

  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) return
    scrollToFiltersIfNeeded()
  }, [selectedCategories, scrollToFiltersIfNeeded])

  const results = useMemo(() => {
    const categoryFiltered =
      selectedCategories && selectedCategories.length > 0 && filterItems
        ? Array.from(
            new Map(
              selectedCategories
                .flatMap((cat) => filterItems(items, cat))
                .map((item) => [item.id, item]),
            ).values(),
          )
        : items

    const trimmedQuery = query.trim()
    if (!trimmedQuery) return categoryFiltered

    const lowerQuery = trimmedQuery.toLowerCase()
    return categoryFiltered.filter((item) => {
      const itemName = item.name?.toLowerCase() || ''
      const app = typeof item.app === 'object' && item.app ? item.app : null
      const appName = app?.name?.toLowerCase() || ''
      return itemName.includes(lowerQuery) || appName.includes(lowerQuery)
    })
  }, [items, selectedCategories, filterItems, query])

  if (items.length === 0) return null

  return (
    <article id="filters" className={cn(overlay && 'overlay scroll-mt-20 pt-4 md:pt-6')}>
      <Filters
        collection={collection}
        filters={filteredCategories}
        handleSelected={handleSelected}
        sticky={true}
        picker={picker}
        fold={fold}
        overlay={overlay}
      />
      <Container reduced={true}>
        <AnimatePresence mode="wait">
          {isFeatured ? (
            <Transition key="featured" direction="left">
              <FeaturedView
                featuredSections={featuredItems}
                element={element}
                handleSelected={handleSelected}
                categories={filteredCategories}
                collection={collection}
              />
            </Transition>
          ) : isAll ? (
            <Transition key="all" direction="right">
              <List
                cols={cols}
                items={results}
                ListItem={listElement || element}
                className="mt-2 md:mt-7"
              />
            </Transition>
          ) : null}
        </AnimatePresence>

        <ExploreButton
          label={`Explore ${isFeatured ? 'all' : 'featured'} ${collection}`}
          onClick={() =>
            handleSelected({
              id: 0,
              name: isFeatured ? 'All' : 'Featured',
              slug: isFeatured ? 'all' : 'featured',
            })
          }
        />
      </Container>
    </article>
  )
}

export function Transition({
  children,
  direction,
}: {
  children: ReactNode
  direction: 'left' | 'right'
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: direction === 'left' ? 20 : -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: direction === 'left' ? 20 : -20 }}
      transition={easeShort}
      className="hide-scrollbar flex flex-col gap-12"
    >
      {children}
    </motion.div>
  )
}

export function FeaturedView<T>({
  featuredSections,
  element: ElementComponent,
  handleSelected,
  categories,
  collection,
}: {
  featuredSections: FeaturedSection[]
  element: ComponentType<{
    item: T
    type?: FeaturedTagType | string
    index?: number
    total?: number
  }>
  handleSelected: (selected: CategoryFilter) => void
  categories: CategoryFilter[]
  collection: string
}) {
  return (
    <div className="grid grid-cols-1 gap-y-5 [overflow-anchor:none] md:grid-cols-2 md:gap-12">
      {featuredSections.map((section, index) => {
        switch (section.sectionType) {
          case FeaturedTagType.Categories:
            if (!categories.length) return
            return (
              <Categories
                key={index}
                header={{
                  title: section.sectionName,
                  subtitle: section.sectionSubtitle || undefined,
                  buttons: [
                    {
                      label: 'See all',
                      click: () => handleSelected({ id: 0, name: 'All', slug: 'all' }),
                    },
                  ],
                  fullWidth: section.pageWidth === 'full',
                }}
                categories={categories}
                collection={collection}
                handleSelected={handleSelected}
              />
            )
          case FeaturedTagType.List:
            return (
              <div key={index} className="col-span-2 md:col-span-1">
                <List
                  header={{
                    title: section.sectionName,
                    subtitle: section.sectionSubtitle || undefined,
                    buttons: [
                      {
                        label: 'See all',
                        click: () => handleSelected({ id: 0, name: 'All', slug: 'all' }),
                      },
                    ],
                    fullWidth: section.pageWidth === 'full',
                  }}
                  items={section.items as ListItemBase[]}
                  ListItem={ElementComponent as React.ComponentType<{ item: ListItemBase }>}
                />
              </div>
            )
          default:
            return (
              <Row
                key={index}
                header={{
                  title: section.sectionName,
                  buttons: [
                    {
                      label: 'See all',
                      click: () => handleSelected({ id: 0, name: 'All', slug: 'all' }),
                    },
                  ],
                  fullWidth: section.pageWidth === 'full',
                }}
              >
                {(section.items || []).map((item, index) => {
                  if (!item || !item.id) return null

                  return (
                    <div
                      key={item.id}
                      className={cn(
                        collection === 'deals' && section.sectionType === 'thumbnailSecondary'
                          ? 'w-auto md:w-full'
                          : 'w-11/12 shrink-0 xs:min-w-[325px] xs:shrink md:min-w-0',
                      )}
                    >
                      <ElementComponent
                        item={item as T}
                        type={section.sectionType}
                        index={index}
                        total={section.items?.length}
                      />
                    </div>
                  )
                })}
              </Row>
            )
        }
      })}
    </div>
  )
}
